import { For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import type { CapabilitySwitches as Caps } from '../../lib/capability-switches';
import { useCardControls } from '../../lib/card-controls-context';
import type { CapZoneView } from '../../lib/machine-capabilities';
import type { CardView } from '../../lib/machine-card';
import { CapabilitySwitches } from './CapabilitySwitches';

// Capabilities (#6785, #6766). For an admin on an own-org machine whose read carries the
// machine's `roles[]` (#6757), the zone is the nine switches (`CapabilitySwitches`), its side
// line "{n} running · {on} of {total} on". Everyone else (a member or a viewer, another org's
// machine, a read with no `roles`) keeps the read-only chips #6785 shipped (owner rulings
// G21, G23; deviation 4): the roles today's claim can give this machine's `mode`
// (`capabilityChips`), with the Agents page's names.

/** The switches' side line: "{on} of {total} on", led by "{n} running ·" while any role runs. */
function switchesSide(zone: CapZoneView): string {
  const counts = { on: zone.on, total: zone.total };
  return zone.running === 0
    ? t('fleet.card.caps.count', counts)
    : t('fleet.card.caps.count_running', { ...counts, n: zone.running });
}

export function CapabilitiesZone(props: { readonly view: CardView }): JSX.Element {
  const handle = useCardControls();
  /** The switches and their view, or null where the chips show. */
  const switches = (): { readonly caps: Caps; readonly zone: CapZoneView } | null => {
    if (handle?.on() !== true) return null;
    const zone = handle.caps.view();
    return zone === null ? null : { caps: handle.caps, zone };
  };
  const side = (): string | null => {
    const s = switches();
    return s === null ? props.view.capabilities.running : switchesSide(s.zone);
  };
  return (
    <section class="mission-zone" data-zone="caps" aria-label={t('fleet.machines.card_caps')}>
      <p class="mission-zone__label">
        <span>{t('fleet.machines.card_caps')}</span>
        <Show when={side()}>{(s) => <span class="mission-zone__side">{s()}</span>}</Show>
      </p>
      <Show
        when={switches()}
        fallback={
          <ul class="mission-caps">
            <For each={props.view.capabilities.chips}>
              {(chip) => (
                <li class="mission-caps__chip" data-lit={chip.lit ? '' : undefined}>
                  <Show when={chip.lit} fallback={chip.name}>
                    <span class="mission-caps__dot--lit" aria-hidden="true" />
                    <span>{t('fleet.machines.card_cap_lit', { name: chip.name })}</span>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        }
      >
        {(s) => <CapabilitySwitches caps={s().caps} zone={s().zone} />}
      </Show>
    </section>
  );
}
